<script lang="ts" setup>
import type { KnownPackageName } from "@anggunpraya-templates/types";
import type { BadgeProps, PopoverProps, RadioGroupProps } from "@nuxt/ui";
import { useMediaQuery, useWindowScroll } from "@vueuse/core";
import { PREVIEW_PACKAGE_MODE_META, usePreviewPackageMode } from "../../../composables/use-preview-package-mode";

const props = withDefaults(defineProps<{
  opened?: boolean;
  radioGroupUi?: Partial<RadioGroupProps["ui"]>;
  badgeRecommendedProps?: Partial<BadgeProps>;
  popoverUi?: Partial<PopoverProps["ui"]>;
}>(), {
  opened: false,
  radioGroupUi: () => ({}),
  badgeRecommendedProps: () => ({}),
  popoverUi: () => ({}),
});

const DESKTOP_NAV_BREAKPOINT = "(min-width: 640px)";
const TABLET_BREAKPOINT = "(min-width: 640px) and (max-width: 1023px)";
const DEFAULT_TEMPLATE_LAYOUT = {
  topRestDesktop: "5rem",
  topDockedDesktop: "1rem",
  topMobile: "0.75rem",
  scrollThreshold: 112,
};

const popoverContent = {
  side: "bottom" as const,
  align: "end" as const,
  sideOffset: 8,
  collisionPadding: 12,
};

const { isPreviewFlavor, previewPackageMeta, previewPackageModes, previewPackageName, reloadWithPreviewPackage } = usePreviewPackageMode();
const { y } = useWindowScroll();
const hasDesktopNav = useMediaQuery(DESKTOP_NAV_BREAKPOINT);
const isTabletViewport = useMediaQuery(TABLET_BREAKPOINT);

const isReloading = ref(false);
const isOpen = ref(true);

const packageItems = computed(() => {
  return previewPackageModes.map(mode => ({
    label: mode,
    value: mode,
    isRecommended: PREVIEW_PACKAGE_MODE_META[mode].isRecommended,
    description: mode === "Intimate"
      ? "Undangan publik yang lebih ringkas tanpa RSVP."
      : "Undangan privat dengan RSVP dan manajemen tamu.",
  }));
},
);

const triggerHintLabel = computed(() => isOpen.value ? "Tutup" : "Pilih");
const isDockedToTop = computed(() => hasDesktopNav.value && y.value > DEFAULT_TEMPLATE_LAYOUT.scrollThreshold);

const containerClass = computed(() => {
  return [
    "fixed z-120 pointer-events-none flex transition-[top] duration-200 ease-out",
    hasDesktopNav.value
      ? "right-4 inset-x-auto justify-end"
      : "left-3 right-auto inset-x-auto justify-start",
  ].join(" ");
});

const containerStyle = computed(() => {
  if (!hasDesktopNav.value || !props.opened) {
    return {
      top: DEFAULT_TEMPLATE_LAYOUT.topMobile,
    };
  }

  return {
    top: !isTabletViewport.value || isDockedToTop.value
      ? DEFAULT_TEMPLATE_LAYOUT.topDockedDesktop
      : DEFAULT_TEMPLATE_LAYOUT.topRestDesktop,
  };
});

async function onSelectMode(mode: KnownPackageName) {
  if (mode === previewPackageName.value || isReloading.value) {
    return;
  }

  isOpen.value = false;
  isReloading.value = true;

  await new Promise(resolve => setTimeout(resolve, 1250));
  await reloadWithPreviewPackage(mode);
}
</script>

<template>
  <div v-if="isPreviewFlavor">
    <div
      class="font-sans"
      :class="containerClass"
      :style="containerStyle"
    >
      <UPopover
        v-model:open="isOpen"
        arrow
        :content="popoverContent"
        :ui="props.popoverUi"
      >
        <UButton
          color="neutral"
          variant="soft"
          size="sm"
          class="pointer-events-auto min-h-10 max-w-[calc(100vw-5.5rem)] rounded-full border border-white/70 bg-white/90 px-3 py-2 text-left shadow-[0_12px_32px_rgba(15,23,42,0.16)] backdrop-blur-xl sm:max-w-none"
          :disabled="isReloading"
        >
          <div class="flex min-w-0 items-center gap-2.5">
            <UIcon
              name="i-lucide-sliders-horizontal"
              class="size-4 shrink-0 text-neutral-700"
            />

            <div class="min-w-0">
              <h1 class="truncate text-sm font-semibold text-neutral-900 font-serif">
                {{ previewPackageName }}
              </h1>
              <p class="text-xs text-neutral-700">
                Paket Preview
              </p>
            </div>

            <div class="ml-1 shrink-0 text-right">
              <div class="flex items-center justify-end gap-1.5 text-neutral-800">
                <UIcon
                  :name="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                  class="size-4 shrink-0"
                />
                <span class="text-xs font-medium text-neutral-700">
                  {{ triggerHintLabel }}
                </span>
              </div>
            </div>
          </div>
        </UButton>

        <template #content>
          <div class="font-sans w-[min(19rem,calc(100vw-1rem))] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-md bg-white/96 p-3 text-neutral-800 shadow-[0_20px_50px_rgba(15,23,42,0.18)] backdrop-blur-xl sm:w-72 sm:max-h-[min(28rem,calc(100vh-7rem))]">
            <div class="flex items-start justify-between gap-3">
              <div class="flex flex-col gap-0.5">
                <p class="text-xs font-semibold tracking-widest text-neutral-700 uppercase">
                  Paket Preview
                </p>
                <p class="text-sm font-semibold text-neutral-900 font-serif">
                  {{ previewPackageName }}
                </p>
              </div>

              <UButton
                color="neutral"
                variant="soft"
                size="xs"
                square
                class="rounded-full bg-neutral-50 text-neutral-800"
                trailing-icon="i-lucide-x"
                @click="isOpen = false"
              />
            </div>

            <p class="mt-2 text-xs leading-4 text-neutral-800">
              {{ previewPackageMeta.description }}
            </p>

            <p class="mt-2 text-xs leading-4.5 text-neutral-700">
              Tap salah satu mode di bawah untuk pindah preview.
            </p>

            <URadioGroup
              orientation="vertical"
              indicator="start"
              variant="card"
              size="md"
              class="mt-3 cursor-pointer"
              :model-value="previewPackageName"
              :items="packageItems"
              :disabled="isReloading"
              :ui="{
                item: 'cursor-pointer',
                label: 'font-semibold',
                description: 'text-neutral-700 text-xs',
                ...props.radioGroupUi,
              }"
              @update:model-value="onSelectMode($event as KnownPackageName)"
            >
              <template #label="{ item }">
                <div class="flex items-center gap-2">
                  <h2 class="font-serif">
                    {{ item.label }}
                  </h2>

                  <UBadge
                    v-if="item.isRecommended"
                    color="primary"
                    variant="subtle"
                    size="sm"
                    class="rounded-full text-primary-900"
                    v-bind="props.badgeRecommendedProps"
                  >
                    Direkomendasikan
                  </UBadge>
                </div>
              </template>
            </URadioGroup>

            <div class="mt-3 flex items-center justify-between gap-3">
              <p class="text-xs leading-4.5 text-neutral-700 font-medium">
                Ganti mode akan me-refresh preview dari awal.
              </p>

              <UButton
                variant="ghost"
                size="xs"
                class="rounded-full px-2 text-primary-800"
                label="Tutup"
                leading-icon="i-lucide-chevron-up"
                @click="isOpen = false"
              />
            </div>
          </div>
        </template>
      </UPopover>
    </div>

    <!-- Preview Package Loading -->
    <Transition name="preview-package-loading">
      <div
        v-if="isReloading"
        class="fixed inset-0 z-130 flex items-center justify-center bg-neutral-950/55 px-6 backdrop-blur-sm"
      >
        <div class="w-full max-w-xs rounded-4xl border border-white/15 bg-neutral-950/85 px-6 py-7 text-center text-white shadow-[0_24px_80px_rgba(0,0,0,0.4)]">
          <div class="mx-auto size-10 animate-spin rounded-full border-2 border-white/25 border-t-white" />
          <p class="mt-4 text-sm font-semibold tracking-[0.24em] uppercase">
            Memuat Preview
          </p>
          <p class="mt-2 text-sm leading-6 text-white/75">
            Mode paket sedang diganti dan halaman akan dimulai lagi dari awal.
          </p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.preview-package-loading-enter-active,
.preview-package-loading-leave-active {
  transition: opacity 180ms ease;
}

.preview-package-loading-enter-from,
.preview-package-loading-leave-to {
  opacity: 0;
}
</style>
